import { useCallback, useEffect, useState } from 'react';

export const PRIMARY_VIEWS = [
  'chat',
  'projects',
  'images',
  'music',
  'speech',
  'video',
  'documents',
  'yesterday',
  // Stage 33: Bulletin Canvas — endless 2D panel canvas backed by
  // scheduled agentic tasks.
  'bulletin',
  // Stage 44: LIBRARIAN-Wiki — knowledge wiki built from ingested artifacts.
  'wiki',
  // Stage 48: Remote Gallery Connections — operator-only page for minting
  // per-app API tokens that authorise read-only /api/public/v1/* access.
  'connections',
] as const;

export type PrimaryView = (typeof PRIMARY_VIEWS)[number];

function parseHash(): PrimaryView {
  const h = (
    typeof window !== 'undefined' ? window.location.hash : ''
  ).replace(/^#\/?/, '');
  // Stage 44: the wiki route uses sub-paths (#/wiki/p/<pid>/page/<slug>).
  // Match the leading segment so any wiki sub-path keeps the WIKI button
  // active in the top bar.
  const head = h.split('/')[0];
  return (PRIMARY_VIEWS as readonly string[]).includes(head)
    ? (head as PrimaryView)
    : 'chat';
}

/**
 * Stage 12 (A3): lightweight hash-based routing for the five primary views.
 * Replaces `useState<PrimaryView>('chat')` without introducing a router
 * library. Reloads restore the previous view; back/forward buttons navigate
 * the view history.
 */
export function useHashRoute(): [PrimaryView, (next: PrimaryView) => void] {
  const [route, setRoute] = useState<PrimaryView>(() => parseHash());

  useEffect(() => {
    const onChange = (): void => setRoute(parseHash());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const nav = useCallback((next: PrimaryView) => {
    if (parseHash() === next) {
      setRoute(next);
      return;
    }
    window.location.hash = `#/${next}`;
    // The `hashchange` listener will fire and update React state.
  }, []);

  return [route, nav];
}
